import { useState, useEffect } from 'react';
import { FunctionalLocation } from '@easydone/shared';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

interface FlocTreeNode extends FunctionalLocation {
  children?: FlocTreeNode[];
}

interface FlocTreeSelectProps {
  flocs: FunctionalLocation[];
  selectedId: string;
  onSelect: (flocId: string) => void;
  excludeId?: string; // Exclude this FLOC and its descendants (for edit scenarios)
}

export function FlocTreeSelect({ flocs, selectedId, onSelect, excludeId }: FlocTreeSelectProps) {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [treeData, setTreeData] = useState<FlocTreeNode[]>([]);

  useEffect(() => {
    buildTree();
  }, [flocs, excludeId]);

  const buildTree = () => {
    // Filter out excluded FLOC and its descendants
    let filteredFlocs = flocs;
    if (excludeId) {
      const excludedFloc = flocs.find(f => f.id === excludeId);
      if (excludedFloc) {
        // Exclude the FLOC itself and any descendants
        filteredFlocs = flocs.filter(f =>
          f.id !== excludeId &&
          !f.path.includes(excludeId)
        );
      }
    }

    // Build tree structure
    const nodeMap = new Map<string, FlocTreeNode>();
    const rootNodes: FlocTreeNode[] = [];

    // First pass: create all nodes
    filteredFlocs.forEach(floc => {
      nodeMap.set(floc.id, { ...floc, children: [] });
    });

    // Second pass: build parent-child relationships
    filteredFlocs.forEach(floc => {
      const node = nodeMap.get(floc.id)!;
      if (floc.parentFlocId && nodeMap.has(floc.parentFlocId)) {
        const parent = nodeMap.get(floc.parentFlocId)!;
        parent.children!.push(node);
      } else {
        rootNodes.push(node);
      }
    });

    // Sort children by code
    const sortChildren = (nodes: FlocTreeNode[]) => {
      nodes.sort((a, b) => a.code.localeCompare(b.code));
      nodes.forEach(node => {
        if (node.children && node.children.length > 0) {
          sortChildren(node.children);
        }
      });
    };
    sortChildren(rootNodes);

    setTreeData(rootNodes);

    // Auto-expand path to selected node
    if (selectedId) {
      const selectedFloc = flocs.find(f => f.id === selectedId);
      if (selectedFloc) {
        const expanded = new Set(expandedNodes);
        selectedFloc.path.forEach(id => expanded.add(id));
        setExpandedNodes(expanded);
      }
    }
  };

  const toggleExpand = (nodeId: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const renderNode = (node: FlocTreeNode, level: number = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id);
    const isSelected = node.id === selectedId;

    return (
      <div key={node.id}>
        <div
          className={`flex items-center py-2 px-3 rounded cursor-pointer transition-colors ${
            isSelected
              ? 'bg-primary-100 text-primary-900 font-medium'
              : 'hover:bg-gray-100 text-gray-700'
          }`}
          style={{ paddingLeft: `${level * 24 + 12}px` }}
          onClick={() => onSelect(node.id)}
        >
          {hasChildren ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toggleExpand(node.id);
              }}
              className="mr-2 p-0.5 hover:bg-gray-200 rounded"
            >
              {isExpanded ? (
                <ChevronDownIcon className="w-4 h-4" />
              ) : (
                <ChevronRightIcon className="w-4 h-4" />
              )}
            </button>
          ) : (
            <span className="w-5 mr-2" />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm truncate">{node.name}</span>
              {hasChildren && (
                <span className="text-xs text-gray-500">({node.children!.length})</span>
              )}
            </div>
            <div className="text-xs text-gray-500 truncate">{node.code}</div>
          </div>
        </div>
        {hasChildren && isExpanded && (
          <div>
            {node.children!.map(child => renderNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  if (treeData.length === 0) {
    return (
      <div className="text-sm text-gray-500 p-4 text-center">
        No locations available
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto bg-white">
      <div className="py-2">
        {treeData.map(node => renderNode(node))}
      </div>
    </div>
  );
}
